import {
  Button,
  InputLabel,
  TextField,
  TextareaAutosize,
  Typography,
} from "@mui/material";
import Select from "@mui/material/Select/Select";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../app/hooks";
import { RootState } from "../../app/store";
import { getBillingStages, getFees } from "../../app/store/fees/feeSlice";
import { getMatterDetails } from "../../app/store/matter/matterSlice";
import { IFeeDto } from "../../shared/dto/fee.dto";
import { IMatterDetailsDto } from "../../shared/dto/matter-details.dto";
import { close, setWindowTitle } from "../../shared/utils/sdk-utils";
import CurrencyInput from "../components/currency-input";
import LocalDatePicker from "../components/date-picker";
import FeeDeleteButton from "../components/fee-delete-button";
import Footer from "../components/footer";
import TopBar from "../components/topbar";
import { filterInactiveStaff } from "../../shared/utils/utils";
import { useAppInit } from "../../shared/hooks/use-app-init";
import PoaDialog from "../components/poa-dialog";

const PoaFixedFee: FC = () => {
  const listsInitialised = useRef(false);
  const matterDetailsInitialised = useRef(false);
  const feeDetailsInitialised = useRef(false);
  const dispatch = useAppDispatch();

  const matterDetails = useSelector(
    (state: RootState) => state.matter.matterDetails
  );
  const feeDetails = useSelector((state: RootState) => state.fee.fee);
  const billingStages = useSelector(
    (state: RootState) => state.fee.billingStages
  );
  const sdkApi = useSelector((state: RootState) => state.app.sdkApi);
  const appInit = useSelector((state: RootState) => state.app.appInit);
  const sdkInitialised = useSelector(
    (state: RootState) => state.app.sdkInitialised
  );

  const [currentMatterDetails, setCurrentMatterDetails] = useState<
    IMatterDetailsDto | undefined
  >(undefined);
  const [currentFeeDetails, setCurrentFeeDetails] = useState<
    IFeeDto | undefined
  >(undefined);
  const [showFeesDialog, setShowFeesDialog] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const feeId = urlParams.get("feeid");

  const getData = useCallback(() => {
    if (!listsInitialised.current) {
      listsInitialised.current = true;
      dispatch(getMatterDetails(false)).then((matterAction) => {
        if (matterAction.meta.requestStatus !== "rejected") {
          if (feeId) {
            dispatch(getFees([feeId || ""])).then(() => {
              dispatch(
                getBillingStages(
                  (matterAction.payload as IMatterDetailsDto).legalAidObj
                    ?.matterTypeId || 0
                )
              );
            });
          }
        }
      });
    }
  }, [dispatch, feeId]);

  useAppInit(() => {
    getData();
  });

  useEffect(() => {
    if (!!sdkApi && !!sdkInitialised && !!appInit) {
      getData();
    }
  }, [dispatch, sdkApi, sdkInitialised, getData, appInit]);

  useEffect(() => {
    if (!!matterDetails && !matterDetailsInitialised.current) {
      matterDetailsInitialised.current = true;
      setCurrentMatterDetails(matterDetails);
      setWindowTitle(
        `${matterDetails?.matterReference || ""} ${
          matterDetails?.matterDescription || ""
        }`,
        sdkApi,
        60,
        "- Payment On Account"
      );
    }
  }, [matterDetails, matterDetailsInitialised, sdkApi]);

  useEffect(() => {
    if (!!feeDetails && !feeDetailsInitialised.current) {
      feeDetailsInitialised.current = true;
      let currentFee = { ...feeDetails[0] };

      setCurrentFeeDetails(currentFee);
    }
  }, [feeDetails, feeDetailsInitialised]);

  return (
    <>
      <TopBar
        helpUrl="https://community.leap.co.uk/s/article/Legal-Aid-Payment-on-Account"
        leftComponents={<FeeDeleteButton fee={currentFeeDetails} />}
      />
      <div className="main">
        <div className="mainsection">
          <div className="flex1">
            <div className="inputRow">
              <div className="flex1">
                <InputLabel>Matter</InputLabel>
              </div>
              <div className="flex6">
                <div className="displayFlex" style={{ alignItems: "center" }}>
                  <TextField
                    variant="outlined"
                    value={currentMatterDetails?.matterReference || ""}
                    disabled
                    style={{ width: 110, paddingRight: 5 }}
                  />
                  <Typography style={{ maxWidth: 560, minWidth: 560 }} noWrap>
                    {currentMatterDetails?.matterDescription || ""}
                  </Typography>
                </div>
              </div>

              <div className="flex2">
                <div className="displayFlex">
                  <div className="flex1" />
                  <InputLabel style={{ paddingRight: 10 }} required>
                    Date
                  </InputLabel>
                  <div style={{ width: 108 }}>
                    <LocalDatePicker
                      value={
                        currentFeeDetails?.transactionDate
                          ? currentFeeDetails?.transactionDate
                          : null
                      }
                      disabled
                      onUpdate={() => {}}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="inputRow">
              <div className="flex1">
                <InputLabel required>Billing Stage</InputLabel>
              </div>
              <div className="flex2">
                <Select
                  className="select"
                  fullWidth
                  variant="outlined"
                  native
                  value={currentFeeDetails?.legalAidObj.billingStageId || ""}
                  disabled
                >
                  <option key="" value="" className="emptyMenuItem"></option>
                  {[...(billingStages || [])]
                    ?.sort((a, b) => (a.id > b.id ? 1 : -1))
                    .map((billingStage) => (
                      <option key={billingStage.id} value={billingStage.id}>
                        {billingStage.description}
                      </option>
                    ))}
                </Select>
              </div>
              <div className="flex6" />
            </div>
          </div>
        </div>
        <div className="mainsection">
          <div className="flex1">
            <div className="inputRowTextArea">
              <div className="flex1">
                <InputLabel>Billing Description</InputLabel>
              </div>
              <div className="flex8">
                <TextareaAutosize
                  value={currentFeeDetails?.description || ""}
                  minRows={3}
                  maxRows={3}
                  style={{
                    minWidth: "calc(100vw - 152px)",
                    maxWidth: "calc(100vw - 152px)",
                  }}
                  disabled
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mainsection">
          <div className="flex1">
            <div className="displayFlex">
              <div className="flex1" style={{ paddingTop: 1 }}>
                <InputLabel required style={{ display: "contents" }}>
                  Staff
                </InputLabel>
              </div>
              <div className="flex2">
                <div className="displayFlex">
                  <Select
                    className="select"
                    fullWidth
                    native
                    variant="outlined"
                    value={currentFeeDetails?.legalAidObj.feeEarnerId || ""}
                    disabled
                  >
                    {!currentFeeDetails?.legalAidObj.feeEarnerId && (
                      <option
                        key=""
                        value=""
                        className="emptyMenuItem"
                      ></option>
                    )}
                    {filterInactiveStaff(
                      appInit?.firmJson.staff,
                      currentFeeDetails?.legalAidObj.feeEarnerId
                    )?.map((s) => (
                      <option key={s.__id} value={s.__id}>
                        {`${s.firstName} ${s.lastName}`}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="flex4" />
              <div className="flex2">
                <div className="displayFlex">
                  <div className="flex1" />
                  <div style={{ paddingTop: 1 }}>
                    <Typography
                      align="right"
                      className="bold-text"
                      style={{
                        paddingRight: 10,
                        display: "contents",
                      }}
                    >
                      AMOUNT
                    </Typography>
                  </div>
                  <div style={{ paddingLeft: 10 }}>
                    <CurrencyInput
                      disabled
                      value={currentFeeDetails?.amount}
                      prefix="£"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mainsection" style={{ marginTop: 4 }}>
          <div className="flex1">
            <div className="displayFlex">
              <div className="flex7" />
              <div className="flex2">
                <div className="displayFlex">
                  <div className="flex1" />

                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowFeesDialog(true);
                    }}
                    color="secondary"
                    disableRipple
                  >
                    View Items
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer
        onCancel={() => {
          close(sdkApi);
        }}
      />
      <PoaDialog
        isOpen={showFeesDialog}
        onSelect={(items) => {}}
        items={currentFeeDetails?.legalAidObj?.poa?.items}
        onClose={() => {
          setShowFeesDialog(false);
        }}
        disabled={true}
      />
    </>
  );
};

export default PoaFixedFee;
